/** @jsx jsx */
import { Colors } from '@zapier/theme';
import { createKeyHandler } from '@zapier/common-utils';
import {
  Dropdown,
  DropdownDetails,
  Gravatar,
  OutsideClickable,
} from '@zapier/common-components';
import { css, jsx, Interpolation } from '@emotion/core';
import NavLink from './NavLink';
import { HeaderNav, HeaderUser } from '@zapier/product-dev-platform/src/types';
import type { NavItem } from './types';

type UserOptionsProps = {
  ctaLinks: HeaderNav;
  isUserOptionsOpen: boolean;
  onToggleUserOptions: () => void;
  optionsLinks: Array<NavItem>;
  user: HeaderUser;
};
const PERM_OPTIONS = [
  {
    label: 'zapier.com',
    href: 'https://www.zapier.com',
    isExit: true,
  },
];
const avatarWrapCss: Interpolation = {
  display: 'block',
  position: 'relative',
  cursor: 'pointer',
  userSelect: 'none',
};
type DropDownLinkProps = {
  href: string;
  isExit?: boolean;
  label: string;
  onlyMobile?: boolean;
};

const dropdownLinkCss = (props: DropDownLinkProps) =>
  css(
    {
      minWidth: 237,
      a: {
        display: 'block',
        color: Colors.batmobile,
        padding: '9px 18px',
        ':hover': {
          backgroundColor: Colors.adamantium,
          color: Colors.batmobile,
        },
      },
    },
    props.onlyMobile && {
      '@media (min-width: 1079px)': {
        display: 'none',
      },
    }
  );

const DropDownLink = (link: DropDownLinkProps) => (
  <li css={dropdownLinkCss(link)}>
    <NavLink href={link.href} isExit={link.isExit} label={link.label} />
  </li>
);

const Divider = () => (
  <li
    css={{
      borderTop: `1px solid ${Colors.adamantium}`,
    }}
  />
);

const UserOptions = (props: UserOptionsProps) => {
  const links = [...PERM_OPTIONS, ...props.optionsLinks];

  return (
    <OutsideClickable
      isListening={props.isUserOptionsOpen}
      onClickOutside={props.onToggleUserOptions}
    >
      <DropdownDetails
        isOpen={props.isUserOptionsOpen}
        summaryNode={
          <div
            aria-label={props.user.name}
            css={avatarWrapCss}
            onClick={e => {
              /**
               * We must event.preventDefault() because there is an issue syncing react states with native html <detail [open]> state
               * Most likely the synthetic event is triggering <detail [open]> again after the native event fires
               * https://github.com/facebook/react/issues/15486
               **/
              e.preventDefault();
              props.onToggleUserOptions();
            }}
            onKeyDown={createKeyHandler(['enter'], props.onToggleUserOptions)}
            role="button"
            tabIndex={0}
          >
            <Gravatar
              name={props.user.name}
              size={33}
              url={props.user.avatarUrl}
            />
          </div>
        }
      >
        <nav aria-label="Navigation of user options">
          <Dropdown
            horizontal="right"
            maxHeight="20em"
            tag="ul"
            vertical="bottom"
          >
            {props.ctaLinks.map(link => (
              <DropDownLink key={link.label} {...link} onlyMobile={true} />
            ))}
            {links.map(link => (
              <DropDownLink key={link.label} {...link} />
            ))}
            <Divider />
            <DropDownLink href="/logout" label="Log Out" />
          </Dropdown>
        </nav>
      </DropdownDetails>
    </OutsideClickable>
  );
};

UserOptions.defaultProps = {
  ctaLinks: [],
  optionsLinks: [],
};
export default UserOptions;
